import React, { createContext, useReducer, useContext, ReactNode } from 'react';

enum DialogActionType {
  OPEN_DIALOG = 'OPEN_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
}

enum CohortBuilderActionDialogType {
  // TODO: Readd with autosave functionality
  // DELETE_SIMPLE_EVENT = 'deleteSimpleEvent',
  // DELETE_DERIVED_EVENT = 'deleteDerivedEvent',
  // DELETE_SUBJECT_VARIABLE = 'deleteSubjectVariable',
  // DELETE_INDEX_DATE = 'deleteIndexDate',
  PROCESS_COHORT_DEFINITION = 'processCohortDefinition',
  SAVE_COHORT_DEFINITION = 'saveCohortDefinition',
  EXIT_COHORT_DEFINITION = 'exitCohortDefinition',
}

type CohortBuilderModalAction =
  | {
      type: DialogActionType.OPEN_DIALOG;
      payload: {
        dialogType: CohortBuilderActionDialogType;
        itemId: string | null;
        exitAction?: () => void;
      };
    }
  | { type: DialogActionType.CLOSE_DIALOG };

type CohortBuilderModalState = {
  activeDialog: CohortBuilderActionDialogType | null;
  itemId: string | null;
  exitAction: (() => void) | undefined;
};

interface CohortBuilderDialogActionOptions {
  itemId?: string | null;
  exitAction?: () => void;
}

const initialState: CohortBuilderModalState = {
  activeDialog: null,
  itemId: null,
  exitAction: undefined,
};

const CohortBuilderModalContext = createContext<{
  state: CohortBuilderModalState;
  openDialog: (
    dialogType: CohortBuilderActionDialogType,
    options?: CohortBuilderDialogActionOptions
  ) => void;
  closeDialog: () => void;
}>({
  state: initialState,
  openDialog: () => null,
  closeDialog: () => null,
});

const dialogReducer = (
  state: CohortBuilderModalState,
  action: CohortBuilderModalAction
): CohortBuilderModalState => {
  switch (action.type) {
    case DialogActionType.OPEN_DIALOG:
      return {
        activeDialog: action.payload.dialogType,
        itemId: action.payload.itemId,
        exitAction: action.payload.exitAction,
      };
    case DialogActionType.CLOSE_DIALOG:
      return { itemId: null, activeDialog: null, exitAction: undefined };
    default:
      return state;
  }
};

const CohortBuilderModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dialogReducer, initialState);

  const openDialog = (
    dialogType: CohortBuilderActionDialogType,
    options?: CohortBuilderDialogActionOptions
  ) => {
    dispatch({
      type: DialogActionType.OPEN_DIALOG,
      payload: { dialogType, itemId: options?.itemId || null, exitAction: options?.exitAction },
    });
  };

  const closeDialog = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  return (
    <CohortBuilderModalContext.Provider value={{ state, openDialog, closeDialog }}>
      {children}
    </CohortBuilderModalContext.Provider>
  );
};

const useCohortBuilderModalState = () => {
  const { state } = useContext(CohortBuilderModalContext);
  return state;
};

const useCohortBuilderModalDispatch = () => {
  const { openDialog, closeDialog } = useContext(CohortBuilderModalContext);
  return { openDialog, closeDialog };
};

const useCohortBuilderModalContext = () => {
  const state = useCohortBuilderModalState();
  const { openDialog, closeDialog } = useCohortBuilderModalDispatch();
  return { ...state, openDialog, closeDialog };
};

// Only export necessary items
export { CohortBuilderActionDialogType, CohortBuilderModalProvider, useCohortBuilderModalContext };
